// Device Trust Management System
// Handles device fingerprinting and trust status for OTP requirement decisions

/**
 * Generate a unique device fingerprint based on browser/device characteristics
 * @returns {string} - Unique device fingerprint
 */
export const generateDeviceFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint test', 2, 2);
    
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
      canvas: canvas.toDataURL(),
      webGL: getWebGLFingerprint(),
      plugins: Array.from(navigator.plugins).map(p => p.name).sort().join(','),
      localStorage: typeof(Storage) !== "undefined",
      sessionStorage: typeof(Storage) !== "undefined",
      touchSupport: 'ontouchstart' in window,
      deviceMemory: navigator.deviceMemory || 'unknown',
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown'
    };
    
    // Create hash from fingerprint data
    const fingerprintString = JSON.stringify(fingerprint);
    return btoa(fingerprintString).replace(/[^a-zA-Z0-9]/g, '').substring(0, 32);
    
  } catch (error) {
    // Fallback fingerprint
    return btoa(navigator.userAgent + Date.now()).replace(/[^a-zA-Z0-9]/g, '').substring(0, 32);
  }
};

/**
 * Get WebGL fingerprint for more unique device identification
 * @returns {string} - WebGL fingerprint
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return 'no-webgl';
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    
    return `${vendor}-${renderer}`;
  } catch (error) {
    return 'webgl-error';
  }
};

/**
 * Check if device is trusted for a specific user
 * @param {string} userEmail - User's email address
 * @param {string} deviceId - Device fingerprint
 * @returns {Promise<object>} - Trust status and expiration info
 */
export const checkDeviceTrust = async (userEmail, deviceId = null) => {
  try {
    const currentDeviceId = deviceId || generateDeviceFingerprint();
    const firebase = require('../store/firebase').default;
    
    // Get user's trusted devices
    const userKey = userEmail.replace(/\./g, '_').replace(/@/g, '_at_');
    const trustedDevicesRef = firebase.database.ref(`/trusted_devices/${userKey}`);
    const snapshot = await trustedDevicesRef.once('value');
    
    if (!snapshot.exists()) {
  
      return {
        isTrusted: false,
        isNewDevice: true,
        deviceId: currentDeviceId,
        trustExpiry: null,
        daysUntilExpiry: 0
      };
    }
    
    const trustedDevices = snapshot.val();
    const deviceData = trustedDevices[currentDeviceId];
    
    if (!deviceData) {

      return {
        isTrusted: false,
        isNewDevice: true,
        deviceId: currentDeviceId,
        trustExpiry: null,
        daysUntilExpiry: 0
      };
    }
    
    const now = Date.now();
    const trustExpiry = deviceData.trustExpiry;
    const daysUntilExpiry = Math.ceil((trustExpiry - now) / (1000 * 60 * 60 * 24));
    
    // Check if trust has expired (7 days = 604800000 milliseconds)
    if (now > trustExpiry) {
      
      return {
        isTrusted: false,
        isExpired: true,
        deviceId: currentDeviceId,
        trustExpiry: trustExpiry,
        daysUntilExpiry: 0
      };
    }
    
    
    return {
      isTrusted: true,
      isNewDevice: false,
      deviceId: currentDeviceId,
      trustExpiry: trustExpiry,
      daysUntilExpiry: daysUntilExpiry,
      deviceInfo: deviceData
    };
    
  } catch (error) {
    return {
      isTrusted: false,
      isNewDevice: true,
      deviceId: generateDeviceFingerprint(),
      trustExpiry: null,
      daysUntilExpiry: 0,
      error: error.message
    };
  }
};

/**
 * Add device to trusted list after successful OTP verification
 * @param {string} userEmail - User's email address
 * @param {string} deviceId - Device fingerprint
 * @param {object} deviceInfo - Additional device information
 * @returns {Promise<boolean>} - Success status
 */
export const trustDevice = async (userEmail, deviceId = null, deviceInfo = {}) => {
  try {
    const currentDeviceId = deviceId || generateDeviceFingerprint();
    const firebase = require('../store/firebase').default;
    
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const trustExpiry = now + oneWeek;
    
    // Device information to store
    const deviceData = {
      trustExpiry: trustExpiry,
      trustedAt: now,
      lastUsed: now,
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen: `${screen.width}x${screen.height}`,
        ip: 'unknown', // Could be populated server-side
        ...deviceInfo
      },
      trustCount: 1
    };
    
    // Store in Firebase
    const userKey = userEmail.replace(/\./g, '_').replace(/@/g, '_at_');
    const deviceRef = firebase.database.ref(`/trusted_devices/${userKey}/${currentDeviceId}`);
    
    // Check if device already exists to increment trust count
    const existingSnapshot = await deviceRef.once('value');
    if (existingSnapshot.exists()) {
      const existingData = existingSnapshot.val();
      deviceData.trustCount = (existingData.trustCount || 0) + 1;
      deviceData.previousTrustExpiry = existingData.trustExpiry;
    }
    
    await deviceRef.set(deviceData);
    

    
    // Also store in localStorage for quick access
    localStorage.setItem('deviceTrustInfo', JSON.stringify({
      deviceId: currentDeviceId,
      userEmail: userEmail,
      trustExpiry: trustExpiry,
      trustedAt: now
    }));
    
    return true;
    
  } catch (error) {
    return false;
  }
};

/**
 * Remove device from trusted list
 * @param {string} userEmail - User's email address  
 * @param {string} deviceId - Device fingerprint to remove
 * @returns {Promise<boolean>} - Success status
 */
export const untrustDevice = async (userEmail, deviceId = null) => {
  try {
    const targetDeviceId = deviceId || generateDeviceFingerprint();
    const firebase = require('../store/firebase').default;
    
    const userKey = userEmail.replace(/\./g, '_').replace(/@/g, '_at_');
    const deviceRef = firebase.database.ref(`/trusted_devices/${userKey}/${targetDeviceId}`);
    
    await deviceRef.remove();
    
    // Clear localStorage if it's current device
    const localTrustInfo = JSON.parse(localStorage.getItem('deviceTrustInfo') || '{}');
    if (localTrustInfo.deviceId === targetDeviceId) {
      localStorage.removeItem('deviceTrustInfo');
    }
    
    
    return true;
    
  } catch (error) {
    return false;
  }
};

/**
 * Get all trusted devices for a user (for management UI)
 * @param {string} userEmail - User's email address
 * @returns {Promise<object>} - List of trusted devices
 */
export const getUserTrustedDevices = async (userEmail) => {
  try {
    const firebase = require('../store/firebase').default;
    const userKey = userEmail.replace(/\./g, '_').replace(/@/g, '_at_');
    const trustedDevicesRef = firebase.database.ref(`/trusted_devices/${userKey}`);
    const snapshot = await trustedDevicesRef.once('value');
    
    if (!snapshot.exists()) {
      return {};
    }
    
    const devices = snapshot.val();
    const now = Date.now();
    
    // Add computed fields for each device
    Object.keys(devices).forEach(deviceId => {
      const device = devices[deviceId];
      device.daysUntilExpiry = Math.ceil((device.trustExpiry - now) / (1000 * 60 * 60 * 24));
      device.isExpired = now > device.trustExpiry;
      device.isCurrentDevice = deviceId === generateDeviceFingerprint();
    });
    
    return devices;
    
  } catch (error) {
    return {};
  }
};

/**
 * Update last used timestamp for device
 * @param {string} userEmail - User's email address
 * @param {string} deviceId - Device fingerprint  
 * @returns {Promise<boolean>} - Success status
 */
export const updateDeviceLastUsed = async (userEmail, deviceId = null) => {
  try {
    const currentDeviceId = deviceId || generateDeviceFingerprint();
    const firebase = require('../store/firebase').default;
    
    const userKey = userEmail.replace(/\./g, '_').replace(/@/g, '_at_');
    const deviceRef = firebase.database.ref(`/trusted_devices/${userKey}/${currentDeviceId}`);
    
    await deviceRef.update({
      lastUsed: Date.now()
    });
    
    return true;
    
  } catch (error) {
    return false;
  }
};

export default {
  generateDeviceFingerprint,
  checkDeviceTrust,
  trustDevice,
  untrustDevice,
  getUserTrustedDevices,
  updateDeviceLastUsed
}; 